import { DataProvider } from "@acadia-ui/theme";

const isSubString = (testValue, query) => {
	return testValue?.toLowerCase?.()?.includes?.(query?.toLowerCase?.());
};

const stringMatches = (testValue, query) => {
	return testValue?.toLowerCase?.() === query?.toLowerCase?.();
};

export const doSearch = (rows, searchQuery, dataProvider, childDataProviders) => {
	// if we match the search query to the MasterCampaignName, include it in the results
	const masterCampaigns = rows.filter(({ MasterCampaignName }) => isSubString(MasterCampaignName, searchQuery));

	// define the search mechanics for child campaigns
	const SEARCH_CRITERIA = {
		AND: {},
		OR: {
			CampaignName: isSubString,
			CampaignName_Friendly: isSubString,
			Budget: isSubString,
			StartDate: stringMatches,
			EndDate: stringMatches,
			// this criterion ensures that all children are included if a parent is found
			ParentUniqueIDcode: (value) => masterCampaigns?.map(({ MasterCampaignID }) => `${MasterCampaignID}`)?.includes(value)
		}
	};

	// forceInclude will recieve mastercampaigns that have children that match the search query
	const forceInclude = new Set();

	// the core searching happens for the activation campaigns
	dataProvider.childDataProviders = childDataProviders?.map(({ data }) => {
		const newChildData = data?.filter(
			(datum) =>
				Object.entries(SEARCH_CRITERIA.AND).every(([property, strategy]) => strategy(datum[property], searchQuery)) &&
				Object.entries(SEARCH_CRITERIA.OR).some(([property, strategy]) => strategy(datum[property], searchQuery))
		);

		// always include it's parent if a child is found
		if (newChildData.length > 0) {
			forceInclude.add(newChildData[0]?.ParentUniqueIDcode);
		}

		return new DataProvider({ data: newChildData });
	});

	Array.from(forceInclude)?.forEach((masterCampaignID) => {
		masterCampaigns?.push(rows?.find(({ MasterCampaignID }) => MasterCampaignID == masterCampaignID));
	});

	return Array.from(new Set(masterCampaigns));
};

export const shouldFilter = (key, filters) => {
	return key in filters && (filters[key] === true || Object.values(filters[key])?.some((v) => v));
};

export const inferDateRangeFromFilterPresets = (obj) => {
	let startDate = null;
	let endDate = null;
	if (obj) {
		if (shouldFilter("custom-range", obj)) {
			const [start, end] = obj["custom-range"];
			startDate = start ? new Date(start) : null;
			endDate = end ? new Date(end) : null;
		} else {
			const now = new Date();
			const thirtyDaysAgo = new Date();
			thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
			const includeLast30 = shouldFilter("last-30-days", obj);
			if (shouldFilter("this-year", obj)) {
				const thisYear = now.getFullYear();
				const thisYearStart = new Date(thisYear, 0, 1);
				const thisYearEnd = new Date(thisYear, 11, 31);
				startDate = includeLast30 && thirtyDaysAgo < thisYearStart ? thirtyDaysAgo : thisYearStart;
				endDate = thisYearEnd;
			} else if (shouldFilter("this-month", obj)) {
				const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
				const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
				startDate = includeLast30 && thirtyDaysAgo < thisMonthStart ? thirtyDaysAgo : thisMonthStart;
				endDate = thisMonthEnd;
			} else if (includeLast30) {
				startDate = thirtyDaysAgo;
				endDate = new Date();
			}
		}
	}

	return [startDate, endDate];
};

export const doFilter = (rows, filters, dataProvider, childDataProviders, prefix, dataKeyValueFilters) => {
	const forceInclude = [];
	const allChildData = childDataProviders?.map(({ data }) => data) || [];
	let newChildData = allChildData;
	let hasAtLeastOneFilter = false;

	// platform filter
	if (shouldFilter(`${prefix}-filter-platform-name`, filters)) {
		hasAtLeastOneFilter = true;
		const filteredKeyArray = Object.entries(filters[`${prefix}-filter-platform-name`])?.reduce((acc, [key, val]) => {
			return val === true ? [...acc, key] : acc;
		}, []);

		newChildData = newChildData.map((data) => {
			return data?.filter(({ MasterPlatformID }) => {
				return filteredKeyArray?.includes(MasterPlatformID);
			});
		});
	}

	// key-value filter(s)
	if (shouldFilter(`${prefix}-filter-keyValueFilter`, filters)) {
		hasAtLeastOneFilter = true;
		newChildData = newChildData.map((data) => {
			return data?.filter((row) => {
				return Object.entries(filters[`${prefix}-filter-keyValueFilter`])?.every(([keyId, dataValues]) => {
					const dataKey = dataKeyValueFilters?.find(({ value }) => value == keyId);
					const rowValue = row?.[dataKey.DataKeyDescr];
					if (rowValue == null) {
						return false;
					}
					const dataKeyValues = (dataKey.DataValues?.filter(({ value }) => dataValues?.includes(value)) || [])?.map(({ label }) => label);

					return dataKeyValues?.includes(rowValue);
				});
			});
		});

		rows?.forEach(({ MasterCampaignID }) => {
			const shouldInclude = newChildData?.some((childData) => childData?.some((row) => row?.MasterCampaignID == MasterCampaignID));
			if (shouldInclude) {
				forceInclude.push(MasterCampaignID);
			}
		});
	}

	// date filter
	if (shouldFilter(`${prefix}-filter-date`, filters)) {
		hasAtLeastOneFilter = true;
		const [startFilter, endFilter] = inferDateRangeFromFilterPresets(filters[`${prefix}-filter-date`]);

		newChildData = newChildData.map((data) => {
			return data?.filter(({ StartDate, EndDate }) => {
				const startDate = new Date(StartDate);
				const endDate = new Date(EndDate);
				if (startFilter && endFilter) {
					return startDate >= startFilter && endDate <= endFilter;
				}
				if (startFilter) {
					return startDate >= startFilter;
				}
				if (endFilter) {
					return endDate <= endFilter;
				}
				return true;
			});
		});

		rows?.forEach(({ StartDate, EndDate, MasterCampaignID }) => {
			const startDate = new Date(StartDate);
			const endDate = new Date(EndDate);
			if (startFilter && endFilter) {
				if (startDate >= startFilter && endDate <= endFilter) {
					forceInclude.push(MasterCampaignID);
				}
			} else if (startFilter) {
				if (startDate >= startFilter) {
					forceInclude.push(MasterCampaignID);
				}
			} else if (endFilter) {
				if (endDate <= endFilter) {
					forceInclude.push(MasterCampaignID);
				}
			} else {
				forceInclude.push(MasterCampaignID);
			}
		});
	}

	if (hasAtLeastOneFilter) {
		dataProvider.childDataProviders = newChildData?.map((data) => new DataProvider({ data }));
		return Array.from(
			new Set(
				rows?.filter((row, i) => {
					return dataProvider.childDataProviders[i]?.data?.length > 0 || forceInclude.includes(row?.MasterCampaignID);
				})
			)
		);
	}

	return rows;
};
